/**
 * Объекты (беседки) — список с агрегацией расходов по 4 типам и маржой.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import { FinanceLayout } from "@/components/finance/FinanceLayout";
import { ObjectFormModal } from "@/components/finance/ObjectFormModal";
import {
  fetchObjects, deleteObject, getObjectTransactionsSummary,
  fetchOverhead,
  formatRub,
  type ObjectRow, type ObjectTxSummary,
  type OverheadResponse,
} from "@/api/finance";
import { ApiError } from "@/api/client";


/** Чипы выбора периода (по дате закрытия договора). «Все» = без фильтра. */
function PeriodChips({ from, to, onChange }: {
  from: string; to: string; onChange: (f: string, t: string) => void;
}) {
  const today = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const t = iso(today);
  const ago = (n: number) => iso(new Date(today.getTime() - n * 86400000));
  const yStart = iso(new Date(Date.UTC(today.getUTCFullYear(), 0, 1)));
  const presets: [string, string, string][] = [
    ["Все", "", ""],
    ["Сегодня", t, t],
    ["Неделя", ago(6), t],
    ["Месяц", ago(29), t],
    ["Квартал", ago(89), t],
    ["365 дней", ago(364), t],
    ["Этот год", yStart, t],
  ];
  return (
    <>
      {presets.map(([label, f, tt]) => (
        <button key={label}
                className={`chip ${from === f && to === tt ? "active" : ""}`}
                onClick={() => onChange(f, tt)}>
          {label}
        </button>
      ))}
    </>
  );
}

/** ISO 'YYYY-MM-DD' → 'ДД.ММ.ГГГГ'. */
function fmtDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
}


export function FinanceObjectsPage() {
  const qc = useQueryClient();
  const [regionFilter, setRegionFilter] = useState<"" | "Москва" | "Юг">("");
  const [statusFilter, setStatusFilter] = useState<"all" | "working" | "done">("all");
  const [signedFrom, setSignedFrom] = useState<string>("");
  const [signedTo, setSignedTo] = useState<string>("");
  const [nameFilter, setNameFilter] = useState<string>("");
  const [editing, setEditing] = useState<ObjectRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [delModal, setDelModal] = useState<{ obj: ObjectRow; summary: ObjectTxSummary } | null>(null);

  const q = useQuery({
    queryKey: ["finance-objects", regionFilter, signedFrom, signedTo],
    queryFn: () => fetchObjects({
      region: regionFilter || undefined,
      signed_from: signedFrom || undefined,
      signed_to: signedTo || undefined,
    }),
  });
  // Переменные коммерческие расходы за тот же период (накладные без объекта)
  const ovQ = useQuery({
    queryKey: ["finance-overhead", signedFrom, signedTo],
    queryFn: () => fetchOverhead({
      date_from: signedFrom || undefined,
      date_to:   signedTo   || undefined,
    }),
    staleTime: 60_000,
  });

  const delMut = useMutation({
    mutationFn: (args: { id: number; transfer_to?: number; force?: boolean }) =>
      deleteObject(args.id, { transfer_to: args.transfer_to, force: args.force }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["finance-objects"] });
      void qc.invalidateQueries({ queryKey: ["finance-overview"] });
      setDelModal(null);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? `HTTP ${err.status}: ${err.message}` : (err as Error).message;
      window.alert(`Не удалось удалить: ${msg}`);
    },
  });

  if (q.isLoading) return <Spinner size="lg" label="Загрузка объектов…" />;
  if (q.error) {
    const err = q.error;
    if (err instanceof ApiError && err.status === 403) {
      return <div className="alert alert-amber"><div className="alert-body">Доступ только для админа/руководителя</div></div>;
    }
    const msg = err instanceof ApiError ? `HTTP ${err.status}` : (err as Error).message;
    return <div className="alert alert-red"><div className="alert-body">{msg}</div></div>;
  }

  // «Выполнен» берём из производственной карточки (production_status):
  // колонка production_objects.status не выставляется и всегда in_progress.
  const isDone = (o: ObjectRow) =>
    o.production_status === "completed" || !!o.actual_end || o.status === "completed";
  const nf = nameFilter.trim().toLowerCase();
  const items = (q.data || []).filter(o => {
    if (statusFilter !== "all" && (statusFilter === "done" ? !isDone(o) : isDone(o))) return false;
    if (nf && !o.client_name.toLowerCase().includes(nf)) return false;
    return true;
  });
  const totalCash     = items.reduce((s, o) => s + o.cash_received, 0);
  const totalProdExp  = items.reduce((s, o) => s + o.total_expense, 0);
  const prodMargin    = totalCash - totalProdExp;   // производственная маржа = выручка − объектные расходы
  const prodMarginPct = totalCash > 0 ? prodMargin / totalCash * 100 : null;
  const overhead      = ovQ.data ?? { marketing: 0, sales: 0, other: 0, unassigned_production: 0, total: 0 } as OverheadResponse;
  const totalExpense  = totalProdExp + overhead.total;   // общий расход
  const netProfit     = totalCash - totalExpense;        // чистая прибыль
  const overallPct    = totalCash > 0 ? netProfit / totalCash * 100 : null;

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }
  function openEdit(o: ObjectRow) {
    setEditing(o);
    setModalOpen(true);
  }
  /** Открыть карточку объекта в Производстве (на дашборде отдела продаж).
   *  Если нет связанной production-карточки — открываем обычную форму. */
  function openProduction(o: ObjectRow) {
    if (!o.production_project_id) { openEdit(o); return; }
    const host = window.location.host.replace(/^finance\./, "sales.");
    const url = `${window.location.protocol}//${host}/production/${o.production_project_id}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }
  async function confirmDelete(o: ObjectRow) {
    let summary: ObjectTxSummary;
    try {
      summary = await getObjectTransactionsSummary(o.id);
    } catch (err) {
      const msg = err instanceof ApiError ? `HTTP ${err.status}` : (err as Error).message;
      window.alert(`Не удалось проверить операции объекта: ${msg}`);
      return;
    }
    if (summary.revenue_count + summary.expense_count === 0) {
      if (window.confirm(`Удалить объект «${o.client_name}» (${o.period_label || "—"})?`)) {
        delMut.mutate({ id: o.id });
      }
      return;
    }
    setDelModal({ obj: o, summary });
  }

  return (
    <FinanceLayout>
      
      {/* Фильтры */}
      <div className="filter-bar" style={{ marginBottom: "var(--gap)", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <input className="input-sm" placeholder="Поиск по объекту…" value={nameFilter}
               onChange={e => setNameFilter(e.target.value)} style={{ minWidth: 180 }}/>
        <select className="input-sm" value={regionFilter} onChange={e => setRegionFilter(e.target.value as "Москва" | "Юг" | "")} style={{ minWidth: 140 }}>
          <option value="">Все регионы</option>
          <option value="Москва">Москва</option>
          <option value="Юг">Юг</option>
        </select>
        <span style={{ fontSize: 12, color: "var(--mute)", marginLeft: 4 }}>Период:</span>
        <PeriodChips from={signedFrom} to={signedTo} onChange={(f, t) => { setSignedFrom(f); setSignedTo(t); }}/>
        <span style={{ fontSize: 12, color: "var(--mute)", marginLeft: 8 }}>Статус:</span>
        <button className={`chip ${statusFilter === "all" ? "active" : ""}`}
                onClick={() => setStatusFilter("all")}>Все</button>
        <button className={`chip ${statusFilter === "working" ? "active" : ""}`}
                onClick={() => setStatusFilter("working")}>В работе</button>
        <button className={`chip ${statusFilter === "done" ? "active" : ""}`}
                onClick={() => setStatusFilter("done")}>Выполнен</button>
        <button className="btn btn-primary" onClick={openCreate} style={{ marginLeft: "auto" }}>
          <Icon name="plus" size={12}/> Новый объект
        </button>
      </div>

      {/* KPI */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: "var(--gap)" }}>
        <div className="kpi">
          <div className="kpi-label">Объектов</div>
          <div className="kpi-value kpi-value-sm">{items.length}</div>
          <div className="kpi-foot"><span style={{ fontSize: 12, color: "var(--mute)" }}>в выборке</span></div>
        </div>
        <div className="kpi good">
          <div className="kpi-label">Выручка</div>
          <div className="kpi-value kpi-value-sm">
            {formatRub(totalCash, { unit: false })}
            <span className="kpi-unit">{Math.abs(totalCash) >= 1_000_000 ? " млн ₽" : " тыс ₽"}</span>
          </div>
          <div className="kpi-foot"><span style={{ fontSize: 12, color: "var(--mute)" }}>Σ приходов по объектам</span></div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Производственные расходы</div>
          <div className="kpi-value kpi-value-sm" style={{ color: "var(--brick)" }}>
            {formatRub(totalProdExp, { unit: false })}
            <span className="kpi-unit">{Math.abs(totalProdExp) >= 1_000_000 ? " млн ₽" : " тыс ₽"}</span>
          </div>
          <div className="kpi-foot"><span style={{ fontSize: 12, color: "var(--mute)" }}>по объектам</span></div>
        </div>
        <div className={`kpi ${prodMargin >= 0 ? "good" : "alarm"}`}>
          <div className="kpi-label">Производственная маржа</div>
          <div className="kpi-value kpi-value-sm">
            {prodMargin >= 0 ? "" : "−"}{formatRub(Math.abs(prodMargin), { unit: false })}
            <span className="kpi-unit">{Math.abs(prodMargin) >= 1_000_000 ? " млн ₽" : " тыс ₽"}</span>
          </div>
          <div className="kpi-foot">
            <span style={{ fontSize: 12, color: "var(--mute)" }}>
              {prodMarginPct !== null ? `${prodMarginPct.toFixed(1)}% от выручки` : "—"}
            </span>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Переменные коммерческие</div>
          <div className="kpi-value kpi-value-sm" style={{ color: "var(--brick)" }}>
            {formatRub(overhead.total, { unit: false })}
            <span className="kpi-unit">{Math.abs(overhead.total) >= 1_000_000 ? " млн ₽" : " тыс ₽"}</span>
          </div>
          <div className="kpi-foot"><span style={{ fontSize: 12, color: "var(--mute)" }}>маркетинг + ОП + прочие</span></div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Общий расход</div>
          <div className="kpi-value kpi-value-sm" style={{ color: "var(--brick)" }}>
            {formatRub(totalExpense, { unit: false })}
            <span className="kpi-unit">{Math.abs(totalExpense) >= 1_000_000 ? " млн ₽" : " тыс ₽"}</span>
          </div>
          <div className="kpi-foot">
            <span style={{ fontSize: 12, color: "var(--mute)" }}>
              {formatRub(totalProdExp, { unit: false })} произв. + {formatRub(overhead.total, { unit: false })} коммерч.
            </span>
          </div>
        </div>
        <div className={`kpi ${netProfit >= 0 ? "good" : "alarm"}`}>
          <div className="kpi-label">Чистая прибыль</div>
          <div className="kpi-value kpi-value-sm">
            {netProfit >= 0 ? "" : "−"}{formatRub(Math.abs(netProfit), { unit: false })}
            <span className="kpi-unit">{Math.abs(netProfit) >= 1_000_000 ? " млн ₽" : " тыс ₽"}</span>
          </div>
          <div className="kpi-foot">
            <span style={{ fontSize: 12, color: "var(--mute)" }}>
              рентабельность {overallPct !== null ? `${overallPct.toFixed(1)}%` : "—"}
            </span>
          </div>
        </div>
      </div>

      {/* Таблица */}
      <div className="card">
        {items.length === 0 ? (
          <div style={{ padding: "32px 20px", textAlign: "center" }}>
            <div style={{ fontFamily: "var(--serif)", fontSize: 18, fontWeight: 700, marginBottom: 6, color: "var(--ink)" }}>
              Объектов не найдено
            </div>
            <button className="btn btn-primary" onClick={openCreate}>
              <Icon name="plus" size={12}/> Новый объект
            </button>
          </div>
        ) : (
          <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Клиент / период</th>
                  <th style={{ width: 60 }}>м²</th>
                  <th className="num" style={{ width: 110 }}>Договор</th>
                  <th className="num" style={{ width: 120 }}>Приход</th>
                  <th className="num" style={{ width: 130 }} title="ФОТ + Материалы + Аутсорс + Налоги по объекту">Расход</th>
                  <th className="num" style={{ width: 120 }} title="Приход − Расход (производственная маржа)">Маржа</th>
                  <th className="num" style={{ width: 90 }} title="Маржа ÷ Приход">Рент-сть</th>
                  <th style={{ width: 80 }}/>
                </tr>
              </thead>
              <tbody>
                {items.map(o => {
                  const margin = o.cash_received - o.total_expense;
                  const marginPct = o.cash_received > 0 ? margin / o.cash_received * 100 : null;
                  return (
                  <tr key={o.id}>
                    <td>
                      <div className="strong">{o.client_name}</div>
                      <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 2 }}>
                        {o.period_label}
                        {o.region && <> · {o.region}</>}
                        {o.is_promotion && <> · <span style={{ color: "var(--brick)" }}>акция</span></>}
                        {" · "}
                        <span className="badge">{isDone(o) ? "Выполнен" : "В работе"}</span>
                        {isDone(o) && o.production_completed_at && (
                          <> · завершён {fmtDate(o.production_completed_at)}</>
                        )}
                        {isDone(o) && !o.production_completed_at && o.production_completed_year && (
                          <> · завершён в {o.production_completed_year} г.</>
                        )}
                      </div>
                    </td>
                    <td className="tabular">{o.sqm ?? "—"}</td>
                    <td className="num tabular" style={{ color: "var(--mute)" }}>{formatRub(o.contract_sum)}</td>
                    <td className="num tabular" style={{ color: "var(--green)" }}>{formatRub(o.cash_received)}</td>
                    <td className="num tabular" style={{ color: "var(--brick)" }}
                        title={`ФОТ ${formatRub(o.fot_total)}   ·   Материалы ${formatRub(o.materials_total)}   ·   Аутсорс ${formatRub(o.outsource_total)}   ·   Налоги ${formatRub(o.taxes_total)}`}>
                      {formatRub(o.total_expense)}
                    </td>
                    <td className="num strong tabular" style={{ color: margin >= 0 ? "var(--green)" : "var(--brick)" }}>
                      {margin >= 0 ? "+" : "−"}{formatRub(Math.abs(margin))}
                    </td>
                    <td className="num tabular" style={{ color: marginPct !== null && marginPct < 0 ? "var(--brick)" : "var(--ink-1)" }}>
                      {marginPct !== null ? `${marginPct.toFixed(1)}%` : "—"}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button className="btn btn-sm"
                                title={o.production_project_id ? "Открыть в Производстве" : "Редактировать"}
                                onClick={() => openProduction(o)}>
                          <Icon name="edit" size={12}/>
                        </button>
                        <button className="btn btn-sm" title="Удалить" onClick={() => confirmDelete(o)}
                                style={{ color: "var(--brick)" }}>
                          <Icon name="trash" size={12}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: "2px solid var(--line)" }}>
                  <td colSpan={3}>Итого по объектам ({items.length})</td>
                  <td className="num tabular" style={{ color: "var(--green)" }}>{formatRub(totalCash)}</td>
                  <td className="num tabular" style={{ color: "var(--brick)" }}>{formatRub(totalProdExp)}</td>
                  <td className="num tabular" style={{ color: prodMargin >= 0 ? "var(--green)" : "var(--brick)" }}>
                    {prodMargin >= 0 ? "+" : "−"}{formatRub(Math.abs(prodMargin))}
                  </td>
                  <td className="num tabular">{prodMarginPct !== null ? `${prodMarginPct.toFixed(1)}%` : "—"}</td>
                  <td/>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Переменные коммерческие расходы за период (не привязаны к объектам) */}
      <div className="card" style={{ padding: 18, marginTop: "var(--gap)" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Переменные коммерческие расходы</div>
          <div style={{ fontSize: 12, color: "var(--mute)" }}>
            расходы без привязки к объекту · период{signedFrom && signedTo
              ? `: ${fmtDate(signedFrom)} — ${fmtDate(signedTo)}`
              : ": всё время"}
          </div>
        </div>
        {ovQ.isLoading ? (
          <div className="muted" style={{ padding: 8 }}>Загрузка…</div>
        ) : ovQ.error ? (
          <div className="alert alert-red"><div className="alert-body">{(ovQ.error as Error).message}</div></div>
        ) : (
          <table className="tbl tbl-tight" style={{ fontSize: 13, width: "100%" }}>
            <tbody>
              <OvRow label="Маркетинг" hint="Я.Директ, Авито, перехват" value={overhead.marketing}/>
              <OvRow label="Отдел продаж (ОП)" hint="менеджеры, КЦ, выездной инженер" value={overhead.sales}/>
              <OvRow label="Прочие" hint="AmoCRM, связь, бухгалтер, офис" value={overhead.other}/>
              {overhead.unassigned_production > 0 && (
                <OvRow label="Производство без привязки к объекту"
                       hint="ФОТ / материалы без указанного объекта" value={overhead.unassigned_production}/>
              )}
              <tr style={{ fontWeight: 700, borderTop: "2px solid var(--line)" }}>
                <td>Итого коммерческих</td>
                <td className="num tabular" style={{ color: "var(--brick)", width: 160 }}>{formatRub(overhead.total)}</td>
              </tr>
            </tbody>
          </table>
        )}
        <div style={{ marginTop: 10, fontSize: 11, color: "var(--mute)", lineHeight: 1.5 }}>
          Общий расход за период = производственные по объектам ({formatRub(totalProdExp)}) +
          переменные коммерческие ({formatRub(overhead.total)}) = <b style={{ color: "var(--ink)" }}>{formatRub(totalExpense)}</b>.
          Чистая прибыль = выручка − общий расход = <b style={{ color: netProfit >= 0 ? "var(--green)" : "var(--brick)" }}>
            {netProfit >= 0 ? "" : "−"}{formatRub(Math.abs(netProfit))}
          </b>. Рентабельность общая = {overallPct !== null ? `${overallPct.toFixed(1)}%` : "—"}.
        </div>
      </div>

      <ObjectFormModal
        open={modalOpen}
        initial={editing}
        onClose={() => setModalOpen(false)}
      />

      {delModal && (
        <DeleteWithTransferModal
          obj={delModal.obj}
          summary={delModal.summary}
          allObjects={items}
          busy={delMut.isPending}
          onCancel={() => setDelModal(null)}
          onTransferAndDelete={(toId) => delMut.mutate({ id: delModal.obj.id, transfer_to: toId })}
          onForceDelete={() => delMut.mutate({ id: delModal.obj.id, force: true })}
        />
      )}
    </FinanceLayout>
  );
}

function DeleteWithTransferModal({
  obj, summary, allObjects, busy, onCancel, onTransferAndDelete, onForceDelete,
}: {
  obj: ObjectRow;
  summary: ObjectTxSummary;
  allObjects: ObjectRow[];
  busy: boolean;
  onCancel: () => void;
  onTransferAndDelete: (toId: number) => void;
  onForceDelete: () => void;
}) {
  const candidates = allObjects.filter(c => c.id !== obj.id);
  const [target, setTarget] = useState<number | "">("");
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-window" style={{ maxWidth: 540 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Удаление объекта «{obj.client_name}»</h3>
          <button className="icon-btn" onClick={onCancel} aria-label="Закрыть"><Icon name="x"/></button>
        </div>
        <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="alert alert-amber" style={{ margin: 0 }}>
            <div className="alert-body">
              К объекту привязано:
              <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
                {summary.revenue_count > 0 && <li>Приходов: <b>{summary.revenue_count}</b> ({formatRub(summary.revenue_total)})</li>}
                {summary.expense_count > 0 && <li>Расходов: <b>{summary.expense_count}</b> ({formatRub(summary.expense_total)})</li>}
              </ul>
            </div>
          </div>
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 12, color: "var(--mute)" }}>Перенести операции на другой объект:</span>
            <select className="input" value={target} onChange={e => setTarget(e.target.value ? Number(e.target.value) : "")}>
              <option value="">— выбрать объект —</option>
              {candidates.map(c => (
                <option key={c.id} value={c.id}>
                  #{c.id} · {c.client_name}{c.period_label ? ` · ${c.period_label}` : ""}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="modal-footer" style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>Отмена</button>
          <button
            className="btn btn-amber"
            onClick={() => {
              if (window.confirm(`Удалить «${obj.client_name}» БЕЗ переноса? Операции останутся «осиротевшими».`)) {
                onForceDelete();
              }
            }}
            disabled={busy}
          >
            Удалить без переноса
          </button>
          <button
            className="btn btn-primary"
            onClick={() => target !== "" && onTransferAndDelete(target)}
            disabled={busy || target === ""}
          >
            Перенести и удалить
          </button>
        </div>
      </div>
    </div>
  );
}

function OvRow({ label, hint, value }: { label: string; hint: string; value: number }) {
  return (
    <tr>
      <td>
        {label}
        <span style={{ fontSize: 11, color: "var(--mute)", marginLeft: 8 }}>{hint}</span>
      </td>
      <td className="num tabular" style={{ width: 160, color: "var(--brick)" }}>{formatRub(value)}</td>
    </tr>
  );
}
